import { DragAndDropFormats, FontIcon, FontIcons, useDrag, useDrop } from '@brandfolder/react';
import { PickerDisplayMode, PickerResponse } from 'filestack-js/build/main/lib/picker';
import React, {
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';

import {
  ActionTypes,
  AttachmentActionDispatch,
  FlattenedAttachment
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { useFilestackPicker } from '@components/common/custom_hooks/useFilestackPicker';
import { addFiles } from '@components/common/filestack_uploader/helpers';
import { ActionsDropdown } from '@components/library/dropdown';
import { ActionDropdownItem } from '@components/library/dropdown_item';
import { ActionDropdownItemProps } from '@components/library/dropdown_item/DropdownItemProps';
import Input from '@components/library/inputs/BaseInput';
import { InputType } from '@components/library/inputs/Input.props';

import { getFilenameSansExt, handleThumbnailError, tempAttachmentTemplate } from '../helpers';

// Drag and drop based on this example:
// https://codesandbox.io/s/github/react-dnd/react-dnd/tree/gh-pages/examples_hooks_ts/04-sortable/simple

interface AttachmentListItemProps {
  attachment: FlattenedAttachment;
  dispatch: AttachmentActionDispatch;
  index: number;
  isTemplateAsset: boolean;
}

const AttachmentListItem = ({
  attachment,
  dispatch,
  index,
  isTemplateAsset
}: AttachmentListItemProps): JSX.Element => {
  const [closeDropdown, setCloseDropdown] = useState(false);
  const [renameAction, setRenameAction] = useState(false);

  const [showHoveredImage, setShowHoveredImage] = useState(false);
  const [isThumbHovered, setIsThumbHovered] = useState(false);
  const [isNameHovered, setIsNameHovered] = useState(false);
  const [enableHoveredImage, setEnableHoveredImage] = useState(true);

  const previewRef = useRef(null);
  const dragRef = useRef(null);
  const dragImageRef = useRef(null);

  const extenstionSansPeriod = attachment?.extension?.replace('.', '') || attachment.extension;
  const filenameSansExt = getFilenameSansExt(attachment?.filename, extenstionSansPeriod);

  const handleReplaceAttachment = (files): void => {
    files.map((file) => (
      dispatch({
        type: ActionTypes.ReplaceAttachment,
        payload: {
          index,
          replacement: tempAttachmentTemplate(file)
        }
      })
    ));
    const sweetAlertSuccessOptions = {
      type: 'success',
      title: bfTranslate('Success!'),
      text: bfTranslate('Your attachment has been updated!'),
      customClass: 'attachment-updated',
      showConfirmButton: true,
      allowOutsideClick: true
    };

    const sweetAlertUploadFailedOptions = {
      type: 'error',
      title: bfTranslate('File Upload Failed'),
      text: bfTranslate('An error occurred while uploading this attachment. Please try again or contact support.'),
      showConfirmButton: true,
      allowOutsideClick: true
    };

    if (files.length === 0) {
      return window.swal(sweetAlertUploadFailedOptions, () => { /* do nothing */ });
    }

    return window.swal(sweetAlertSuccessOptions, () => { /* do nothing */ });
  };

  const handleDeleteAttachment = (): void => {
    setCloseDropdown(true);
    const sweetAlertDeleteOptions = {
      title: bfTranslate('Really?'),
      text: `${bfTranslate('Are you sure you want to delete')} ${attachment.filename}?`,
      type: 'warning',
      showCancelButton: true,
      cancelButtonText: bfTranslate('Cancel'),
      confirmButtonText: bfTranslate('Delete'),
      closeOnConfirm: true,
      customClass: 'deleting'
    };

    window.swal(sweetAlertDeleteOptions, (confirm) => {
      if (confirm) {
        dispatch({
          type: ActionTypes.DeleteAttachment,
          payload: { index }
        });
      }
    });
  };

  const onUploadDone = (pickerResponse: PickerResponse): void => {
    addFiles(pickerResponse, handleReplaceAttachment);
  };

  const { loading, picker, render } = useFilestackPicker({
    onUploadDone,
    pickerOptions: {
      displayMode: PickerDisplayMode.overlay,
      maxFiles: 1
    }
  });

  const { device } = BFG.currentUser;
  const disableFirstAttachment = isTemplateAsset && index === 0;
  const isTouchScreen = device === 'mobile-view' || device === 'tablet-view';
  const isDraggable = !disableFirstAttachment && !isTouchScreen;

  useEffect(() => {
    setShowHoveredImage(isThumbHovered || isNameHovered);
  }, [isThumbHovered, isNameHovered]);

  useEffect(() => {
    // accounts for the case when the cursor "catches" and hovers on the hovered image
    // if this happens, the "onMouseEnter" event won't be captured for
    // the attachment list item element that's below the hovered image
    // to handle this case- we hide the hovered image immediately with setEnableHoveredImage(false)
    // so that the attachment list item element can capture the "onMouseEnter" event
    // this useEffect enables it again immediately so it's ready for the next hover
    if (!enableHoveredImage) {
      setEnableHoveredImage(true);
    }
  }, [enableHoveredImage]);

  const handleRename = (): void => {
    setCloseDropdown(true);
    setRenameAction(true);
  };

  const handleReplace = (): void => {
    setCloseDropdown(true);
    render();
  };

  useEffect(() => {
    if (picker) {
      picker.open();
    }
  }, [picker]);

  let actionsMenuInputs: (ActionDropdownItemProps & { content: string })[] = [
    {
      content: bfTranslate('Delete'),
      icon: 'bff-trash',
      isWarningItem: true,
      name: 'delete',
      onChoose: handleDeleteAttachment,
    },
  ];

  if (extenstionSansPeriod !== 'bftemplate') {
    actionsMenuInputs = [
      {
        content: bfTranslate('Rename'),
        icon: 'bff-edit',
        name: 'rename',
        onChoose: handleRename,
      },
      {
        content: bfTranslate('Replace'),
        icon: 'bff-upload',
        name: 'replace',
        onChoose: handleReplace,
      },
      ...actionsMenuInputs
    ];
  }

  const actionItems = actionsMenuInputs.map((action) => (
    <ActionDropdownItem
      key={action.name}
      icon={action.icon}
      isWarningItem={action.isWarningItem}
      name={action.name}
      onChoose={action.onChoose}
    >
      {action.content}
    </ActionDropdownItem>
  ));

  useDrop({
    format: DragAndDropFormats.TextPlain,
    onDropped: (data: string, e: DragEvent): void => {
      if (!previewRef.current) {
        return;
      }

      const parsedData = data.split('|');
      const draggedIndex = parseInt(parsedData[0]);
      const hoveredIndex = index;

      if (draggedIndex === hoveredIndex) {
        return;
      }

      const hoverBoundingRect = previewRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const hoverClientY = e.y - hoverBoundingRect.top; // pixels from top

      // Dragging downwards
      if (draggedIndex < hoveredIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (draggedIndex > hoveredIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      dispatch({
        type: ActionTypes.RepositionAttachment,
        payload: { draggedIndex, hoveredIndex },
      });
    },
    ref: previewRef
  });

  const { dragging } = useDrag({
    data: `${index}|${attachment.key}`,
    dragImageRef,
    effectAllowed: 'move',
    format: DragAndDropFormats.TextPlain,
    ref: dragRef,
  });

  const displayedImage = attachment.thumbnailUrl || attachment.url;

  return (
    <div ref={isDraggable ? previewRef : undefined}>
      <li
        ref={isDraggable ? dragImageRef : undefined}
        className={`attachment-item ${dragging ? 'is-dragging' : ''}`}
      >
        {isDraggable && (
          <button
            ref={dragRef}
            aria-label={`${attachment.filename} reorder drag button`}
            className="attachment-item--reorder-button"
            type="button"
          >
            <FontIcon icon={FontIcons.DragIndicator} />
          </button>
        )}
        <div
          className={isDraggable
            ? 'attachment-item--thumbnail attachment-item--thumbnail--is-draggable'
            : 'attachment-item--thumbnail'
          }
          onMouseEnter={!isTouchScreen ? ((): void => setIsThumbHovered(true)) : undefined}
          onMouseLeave={!isTouchScreen ? ((): void => setIsThumbHovered(false)) : undefined}
        >
          <img
            alt={attachment.filename}
            className="attachment-item--thumbnail__img"
            data-private
            draggable={false}
            onError={(event: SyntheticEvent<HTMLImageElement, Event>): void => {
              handleThumbnailError(
                event,
                extenstionSansPeriod,
                filenameSansExt
              );
            }}
            src={attachment.thumbnailUrl || attachment.url}
          />
        </div>
        {renameAction ? (
          <>
            <Input
              attributes={{
                autoFocus: true,
                className: 'attachment-item--filename',
                name: 'filename',
                onKeyPress: (e: KeyboardEvent<HTMLInputElement>): void => {
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    (e.target as HTMLElement).blur();
                  }
                },
                onBlur: (): void => setRenameAction(false),
                onChange: (e: InputChangeEvent): void => (
                  dispatch({
                    type: ActionTypes.UpdateAttachmentDetails,
                    payload: {
                      index,
                      filename: `${e.target.value}.${extenstionSansPeriod}`,
                    }
                  })
                ),
                type: InputType.Text,
              }}
              input={{ error: '', value: filenameSansExt }}
            />
            <span>{`.${extenstionSansPeriod}`}</span>
          </>
        ) : (
          <h4
            className={isDraggable
              ? 'attachment-item--filename attachment-item--filename--is-draggable'
              : 'attachment-item--filename'
            }
            onMouseEnter={!isTouchScreen ? ((): void => setIsNameHovered(true)) : undefined}
            onMouseLeave={!isTouchScreen ? ((): void => setIsNameHovered(false)) : undefined}
            title={attachment.filename}
          >
            {attachment.filename}
          </h4>
        )}
        {!disableFirstAttachment && (
          <ActionsDropdown
            className="attachment-item--menu-container"
            closeCallback={(): void => setCloseDropdown(false)}
            disabled={loading}
            openOnClick={false}
            openOnHover
            toggleDropdown={closeDropdown}
            userDevice={BFG.currentUser.device}
          >
            {actionItems}
          </ActionsDropdown>
        )}
        {enableHoveredImage && (
          <CSSTransition
            classNames="fade-in"
            in={showHoveredImage}
            timeout={200}
            unmountOnExit
          >
            <div
              aria-hidden
              className="attachment-item--hovered-container"
              onMouseEnter={!isTouchScreen ? ((): void => setEnableHoveredImage(false)) : undefined}
            >
              <img
                alt={attachment.filename}
                className="hovered-image"
                data-private
                onError={(event: SyntheticEvent<HTMLImageElement, Event>): void => {
                  handleThumbnailError(
                    event,
                    extenstionSansPeriod,
                    filenameSansExt,
                    undefined, // isLargeView
                    true
                  );
                }}
                src={displayedImage}
              />
            </div>
          </CSSTransition>
        )}
      </li>
    </div>
  );
};

export default AttachmentListItem;
